/**
 * Media Tabs Component
 *
 * Provides tab-based filtering for media types in the asset grid.
 */

import { useRef, useEffect } from 'react';
import { MediaType } from '../types';
import { ZprTabs } from '@zpr-stencil';

export interface MediaTabsProps {
  /** Available media types to display as tabs */
  mediaTypes: MediaType[];
  /** Currently selected media type */
  activeType?: MediaType | null;
  /** Callback when a tab is clicked */
  onTabChange?: (mediaType: MediaType | null) => void;
  /** Custom CSS class */
  className?: string;
}

const TAB_LABELS: Record<MediaType, string> = {
  [MediaType.Image]: 'Images',
  [MediaType.Video]: 'Videos',
  [MediaType.Audio]: 'Audio',
  [MediaType.Model3D]: '3D',
  [MediaType.Video360]: '360 Videos',
  [MediaType.Image360]: '360 Images',
  [MediaType.HDRI]: 'HDRIs',
  [MediaType.Texture]: 'Textures',
  [MediaType.Font]: 'Fonts',
  [MediaType.Avatar]: 'Avatars',
  [MediaType.Unknown]: 'Other',
};

export const MediaTabs = ({
  mediaTypes,
  activeType = null,
  onTabChange,
  className = '',
}: MediaTabsProps) => {
  const tabsRef = useRef<any>(null);

  // Tab index 0 = All (null), index N = mediaTypes[N-1]
  const allTabs = [null, ...mediaTypes] as (MediaType | null)[];

  const handleTabsChange = (event: CustomEvent) => {
    onTabChange?.(allTabs[event.detail.id] ?? null);
  };

  // Sync external activeType changes to the zpr-tabs component
  useEffect(() => {
    if (!tabsRef.current) return;
    const idx = allTabs.indexOf(activeType ?? null);
    if (idx >= 0) tabsRef.current.selectTab(idx.toString());
  }, [activeType]);

  return (
    <ZprTabs
      ref={tabsRef}
      isEmpty
      onTabsChange={handleTabsChange}
      className={`media-tabs ${className}`}
    >
      <div tab-header="All" className={activeType === null ? 'active' : ''} />
      {mediaTypes.map((mediaType) => (
        <div
          key={mediaType}
          tab-header={TAB_LABELS[mediaType] ?? mediaType}
          className={activeType === mediaType ? 'active' : ''}
        />
      ))}
    </ZprTabs>
  );
};
